// ============================================================
// JobDashboard.tsx — entity + system name on every job row
// ============================================================
// CHANGES vs uploaded version:
//   1. JobDashboardProps gains `dmData?: any[]` — same array JobPage
//      passes down; used to build entityId→{label,system} lookup.
//   2. buildEntityMap() helper creates the lookup once from dmData.
//   3. RecentJobs rows show entity chip below job type.
//   4. AgenticUsageSection table gets an Entity column.
//   5. TimelineCard tooltip includes entity name.
//   6. DQCard ranked list shows entity chip.
//   7. useAgg gains per-entity breakdown so the "by type" card can show
//      entity distribution when multiple entities have the same job type.
//   8. New EntityBadge component — small teal chip, consistent everywhere.
// ============================================================

import { parseServerTime } from "./serverTime";
import React, { useMemo, useCallback, useRef, useEffect } from "react";
import { Chart, registerables } from "chart.js";
Chart.register(...registerables);

const T = {
  bg:          "#eef1f6", bgCard:"#ffffff", bgSidebar:"#f5f7fa",
  textPrimary: "#0f1923", textSecond:"#1e2d3d", textMuted:"#445566", textLabel:"#2c3e52",
  brand:       "#1e3a6e", brandLight:"#dde7f5", brandMid:"#90aed0", border:"#c8d4e0",
  green:       "#166534", greenBg:"#f0fdf4", greenBorder:"#86efac",
  amber:       "#7c2d00", amberBg:"#fff7ed", amberBorder:"#fde68a",
  red:         "#991b1b", redBg:"#fef2f2",   redBorder:"#fca5a5",
  purple:      "#6b21a8", purpleBg:"#faf5ff", purpleBorder:"#d8b4fe",
  teal:        "#0f766e", tealBg:"#f0fdfa",   tealBorder:"#99f6e4",
};

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500&family=Sora:wght@400;500;600;700&display=swap');
.db*{box-sizing:border-box;margin:0;padding:0;}
.db{font-family:'Sora',sans-serif;background:${T.bg};color:${T.textPrimary};padding:20px 24px;height:100%;overflow-y:auto;font-size:13px;}
.db::-webkit-scrollbar{width:4px;}.db::-webkit-scrollbar-thumb{background:${T.border};border-radius:99px;}
.db-topbar{display:flex;align-items:center;justify-content:space-between;margin-bottom:18px;flex-wrap:wrap;gap:8px;}
.db-title{font-size:15px;font-weight:700;color:${T.textPrimary};}
.db-sub{font-size:11px;color:${T.textMuted};margin-top:2px;}
.db-kpis{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:10px;margin-bottom:14px;}
.db-kpi{background:${T.bgCard};border:1px solid ${T.border};border-radius:9px;padding:13px 15px;position:relative;overflow:hidden;cursor:pointer;transition:border-color .15s,transform .1s;}
.db-kpi:hover{border-color:${T.brandMid};transform:translateY(-1px);}
.db-kpi-accent{position:absolute;top:0;left:0;width:3px;height:100%;border-radius:9px 0 0 9px;}
.db-kpi-lbl{font-size:10px;font-weight:600;letter-spacing:.6px;text-transform:uppercase;color:${T.textMuted};margin-bottom:6px;}
.db-kpi-val{font-size:24px;font-weight:700;line-height:1;font-family:'IBM Plex Mono',monospace;}
.db-kpi-sub{font-size:11px;color:${T.textMuted};margin-top:4px;}
.db-kpi-hint{font-size:9.5px;color:${T.brandMid};margin-top:5px;}
.db-row2{display:grid;grid-template-columns:2fr 1fr;gap:12px;margin-bottom:12px;}
.db-row-h{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:12px;}
.db-card{background:${T.bgCard};border:1px solid ${T.border};border-radius:9px;padding:13px 15px;}
.db-card-title{font-size:10px;font-weight:700;letter-spacing:.8px;text-transform:uppercase;color:${T.brand};margin-bottom:12px;display:flex;align-items:center;justify-content:space-between;}
.db-card-title span{font-size:10px;font-weight:500;color:${T.textMuted};text-transform:none;letter-spacing:0;}
.db-pill{display:inline-flex;align-items:center;padding:1px 7px;border-radius:99px;font-family:'IBM Plex Mono',monospace;font-size:9.5px;font-weight:500;border:1px solid;}
.db-entity-chip{display:inline-flex;align-items:center;padding:1px 6px;border-radius:4px;font-family:'IBM Plex Mono',monospace;font-size:9px;font-weight:600;border:1px solid ${T.tealBorder};background:${T.tealBg};color:${T.teal};max-width:140px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.db-hbar-row{display:flex;align-items:center;gap:8px;margin-bottom:2px;}
.db-hbar-label{font-size:11px;color:${T.textLabel};width:130px;flex-shrink:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.db-hbar-track{flex:1;height:6px;background:${T.bg};border-radius:99px;overflow:hidden;}
.db-hbar-fill{height:100%;border-radius:99px;transition:width .4s ease;}
.db-hbar-count{font-family:'IBM Plex Mono',monospace;font-size:10px;color:${T.textMuted};width:28px;text-align:right;flex-shrink:0;}
.db-type-row{display:flex;align-items:center;gap:8px;padding:7px 5px;border-bottom:1px solid ${T.bg};cursor:pointer;border-radius:5px;transition:background .1s;}
.db-type-row:last-child{border:none;}
.db-type-row:hover{background:${T.brandLight};}
.db-job-row{display:flex;align-items:flex-start;gap:7px;padding:6px 5px;border-bottom:1px solid ${T.bg};cursor:pointer;border-radius:5px;transition:background .1s;}
.db-job-row:last-child{border:none;}
.db-job-row:hover{background:${T.brandLight};}
.db-job-dot{width:7px;height:7px;border-radius:50%;flex-shrink:0;margin-top:4px;}
.db-job-name{flex:1;font-size:11.5px;color:${T.textLabel};overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.db-job-meta{font-family:'IBM Plex Mono',monospace;font-size:10px;color:${T.textMuted};flex-shrink:0;}
.db-drill{all:unset;box-sizing:border-box;display:inline-flex;align-items:center;gap:4px;font-size:11px;color:${T.brand};cursor:pointer;padding:2px 7px;border-radius:4px;transition:background .12s;}
.db-drill:hover{background:${T.brandLight};}
.db-notice{background:${T.brandLight};border:1px solid ${T.brandMid};border-radius:7px;padding:7px 11px;font-size:11px;color:${T.brand};display:flex;gap:6px;align-items:center;margin-top:10px;}
.db-ring{width:18px;height:18px;border-radius:50%;border:2px solid ${T.border};border-top-color:${T.brand};animation:dbspin .7s linear infinite;}
@keyframes dbspin{to{transform:rotate(360deg)}}
.db-empty{display:flex;align-items:center;justify-content:center;height:200px;color:${T.textMuted};font-size:13px;gap:8px;}
`;

// ─── Entity map builder ───────────────────────────────────────────────────────
interface EntityInfo { label: string; system: string; }
type EntityMap = Record<string, EntityInfo>;

function buildEntityMap(dmData?: any[]): EntityMap {
  if (!dmData?.length) return {};
  const map: EntityMap = {};
  dmData.forEach(e => {
    const id = e.entityId || e.id || "";
    if (!id) return;
    map[id] = {
      label:  e.label || e.name || id,
      system: e.dataSrcSystem?.srcSystemName || e.dataSrcSystem?.srcSystemId || "",
    };
  });
  return map;
}

function getEntityInfo(job: any, emap: EntityMap): EntityInfo | null {
  const id = job.entity_id || job.dm_id || "";
  return emap[id] || (id ? { label: id, system: "" } : null);
}

// ─── Entity badge ─────────────────────────────────────────────────────────────
function EntityBadge({ info }: { info: EntityInfo | null }) {
  if (!info) return null;
  const text = info.system ? `${info.system} · ${info.label}` : info.label;
  return (
    <span className="db-entity-chip" title={text}>{text}</span>
  );
}

// ─── Field normalisation ──────────────────────────────────────────────────────
function counts(j: any) {
  const s = j.summary || {};
  return {
    total_rules: s.total_rules ?? j.total_rules ?? 0,
    pass_count:  s.pass_count  ?? j.pass_count  ?? 0,
    fail_count:  s.fail_count  ?? j.fail_count  ?? 0,
  };
}

function isDQ(j: any): boolean { return counts(j).total_rules > 0; }

function isAgentic(j: any): boolean {
  const t = (j.job_type || "").toUpperCase();
  return t.includes("DG_") || t.includes("BUILD_DM") || t.includes("GENERATE_RULES") ||
    t.includes("MERIT") || t.includes("LOOKUP_DETECTION") || t.includes("LINEAGE");
}

const fmtDur = (s?: string | null, e?: string | null) => {
  if (!s) return "—";
  const sec = Math.round(((e ? parseServerTime(e) : new Date()).getTime() - parseServerTime(s).getTime()) / 1000);
  return sec < 60 ? `${sec}s` : `${Math.floor(sec / 60)}m ${sec % 60}s`;
};

const sColor = (s: string) => {
  const sl = (s||"").toLowerCase();
  if (["success","completed","done"].includes(sl)) return T.green;
  if (["failed","fail","error"].includes(sl))      return T.red;
  if (["running","started","in_progress"].includes(sl)) return T.brand;
  if (sl === "partial") return T.amber;
  return T.textMuted;
};
const sBg     = (s:string) => { const c=sColor(s); return c===T.green?T.greenBg:c===T.red?T.redBg:c===T.amber?T.amberBg:T.brandLight; };
const sBorder = (s:string) => { const c=sColor(s); return c===T.green?T.greenBorder:c===T.red?T.redBorder:c===T.amber?T.amberBorder:T.brandMid; };

const Pill = ({ s }: { s: string }) => (
  <span className="db-pill" style={{ background:sBg(s), color:sColor(s), borderColor:sBorder(s) }}>
    {(s||"—").toLowerCase()}
  </span>
);
const Drill = ({ label, onClick }: { label:string; onClick:()=>void }) => (
  <button className="db-drill" onClick={e => { e.stopPropagation(); onClick(); }}>{label} →</button>
);

// ─── Aggregation ──────────────────────────────────────────────────────────────
function useAgg(jobs: any[]) {
  return useMemo(() => {
    const total   = jobs.length;
    const succ    = jobs.filter(j => j.status?.toLowerCase() === "success").length;
    const fail    = jobs.filter(j => ["failed","fail","error"].includes(j.status?.toLowerCase())).length;
    const running = jobs.filter(j => ["running","started","in_progress"].includes(j.status?.toLowerCase())).length;

    const dqJobs  = jobs.filter(isDQ);
    let totRules=0, totPass=0, totFail=0;
    dqJobs.forEach(j => { const c=counts(j); totRules+=c.total_rules; totPass+=c.pass_count; totFail+=c.fail_count; });
    const dqHealth = totRules > 0 ? Math.round(totPass/totRules*100) : null;

    const agJobs = jobs.filter(isAgentic);

    let totChunks=0, succChunks=0, failChunks=0;
    jobs.forEach(j => {
      const st = j.config?.agent_stat;
      if (st) { totChunks+=st.total_chunks||0; succChunks+=st.succeeded_chunks||0; failChunks+=st.failed_chunks_count||0; }
    });

    let totPr=0, totCo=0, totTok=0, totCost=0;
    jobs.forEach(j => {
      const au = j.agentic_usage || {};
      totPr   += au.prompt_tokens     || 0;
      totCo   += au.completion_tokens || 0;
      totTok  += au.total_tokens      || 0;
      totCost += au.cost_usd          || 0;
    });

    const byTypeTok: Record<string, { tok:number; pr:number; co:number; runs:number; succRuns:number }> = {};
    jobs.forEach(j => {
      const t = j.job_type || "OTHER";
      const au = j.agentic_usage || {};
      if (!byTypeTok[t]) byTypeTok[t] = { tok:0, pr:0, co:0, runs:0, succRuns:0 };
      byTypeTok[t].tok  += au.total_tokens      || 0;
      byTypeTok[t].pr   += au.prompt_tokens     || 0;
      byTypeTok[t].co   += au.completion_tokens || 0;
      byTypeTok[t].runs++;
      if ((j.status||"").toLowerCase() === "success") byTypeTok[t].succRuns++;
    });

    const ratio = totTok > 0 ? Math.round((totPr / totTok) * 100) : 50;

    const jobsWithUsage = jobs
      .filter(j => (j.agentic_usage?.total_tokens || 0) > 0)
      .sort((a, b) => (b.agentic_usage?.total_tokens || 0) - (a.agentic_usage?.total_tokens || 0));

    const byType: Record<string, {total:number;succ:number;fail:number}> = {};
    jobs.forEach(j => {
      const t = j.job_type || "OTHER";
      if (!byType[t]) byType[t] = {total:0,succ:0,fail:0};
      byType[t].total++;
      if (j.status?.toLowerCase() === "success") byType[t].succ++;
      if (["failed","fail","error"].includes(j.status?.toLowerCase())) byType[t].fail++;
    });

    const recent = jobs.slice()
      .sort((a,b) => parseServerTime(b.start_time).getTime()-parseServerTime(a.start_time).getTime())
      .slice(0,7);

    const timeline = jobs.slice()
      .sort((a,b) => parseServerTime(a.start_time).getTime()-parseServerTime(b.start_time).getTime());

    const dqRanked = dqJobs.map(j => {
      const c = counts(j);
      const pct = c.total_rules > 0 ? Math.round(c.pass_count/c.total_rules*100) : 0;
      return { ...j, _pass:c.pass_count, _total:c.total_rules, _pct:pct };
    }).sort((a,b)=>b._total-a._total).slice(0,4);

    return { total, succ, fail, running, dqJobs, agJobs, totRules, totPass, totFail, dqHealth,
             totChunks, succChunks, failChunks, byType, recent, timeline, dqRanked,
             totPr, totCo, totTok, totCost, ratio, byTypeTok, jobsWithUsage };
  }, [jobs]);
}

// ─── Charts (unchanged) ───────────────────────────────────────────────────────
const fmtTok = (n?: number | null) =>
  n == null ? "—"
  : n >= 1_000_000 ? `${(n/1_000_000).toFixed(2)}M`
  : n >= 1000      ? `${(n/1000).toFixed(1)}k`
  : String(n);
const fmtUSD = (n?: number | null) =>
  n == null ? "—" : n === 0 ? "$0.00" : `$${n.toFixed(6)}`;

function TokenBarChart({ jobs }: { jobs: any[] }) {
  const ref  = useRef<HTMLCanvasElement>(null);
  const inst = useRef<Chart | null>(null);
  useEffect(() => {
    if (!ref.current || !jobs.length) return;
    if (inst.current) inst.current.destroy();
    const labels = jobs.map(j =>
      (j.job_type||"").replace(/DG_|DQ_/g,"").replace(/_/g," ").toLowerCase().substring(0,18)
    );
    inst.current = new Chart(ref.current, {
      type:"bar",
      data:{ labels, datasets:[
        { label:"Prompt",     data:jobs.map(j=>j.agentic_usage?.prompt_tokens||0),     backgroundColor:"#AFA9EC", borderRadius:3, stack:"s" },
        { label:"Completion", data:jobs.map(j=>j.agentic_usage?.completion_tokens||0), backgroundColor:"#90aed0", borderRadius:3, stack:"s" },
      ]},
      options:{
        indexAxis:"y" as const, responsive:true, maintainAspectRatio:false,
        plugins:{ legend:{display:false}, tooltip:{callbacks:{label:(ctx:any)=>`${ctx.dataset.label}: ${fmtTok(ctx.parsed.x)} tok`}}},
        scales:{
          x:{ticks:{font:{size:10},color:"#445566",callback:(v:any)=>v>=1000?`${(v/1000).toFixed(0)}k`:v},grid:{color:"#eef1f6"},stacked:true},
          y:{ticks:{font:{size:10},color:"#445566",autoSkip:false},grid:{display:false},stacked:true},
        },
      },
    });
    return () => { if (inst.current) inst.current.destroy(); };
  }, [jobs]);
  return <div style={{position:"relative",height:Math.max(jobs.length*44+50,100)}}><canvas ref={ref}/></div>;
}

function TypeTokenChart({ byTypeTok }: { byTypeTok: Record<string,any> }) {
  const ref  = useRef<HTMLCanvasElement>(null);
  const inst = useRef<Chart | null>(null);
  const entries = Object.entries(byTypeTok).filter(([,v])=>v.tok>0);
  useEffect(() => {
    if (!ref.current||!entries.length) return;
    if (inst.current) inst.current.destroy();
    const labels = entries.map(([t])=>t.replace(/DG_|DQ_/g,"").replace(/_/g," ").toLowerCase().substring(0,14));
    inst.current = new Chart(ref.current, {
      type:"bar",
      data:{ labels, datasets:[
        { label:"Prompt",     data:entries.map(([,v])=>v.pr), backgroundColor:"#AFA9EC", borderRadius:3, stack:"s" },
        { label:"Completion", data:entries.map(([,v])=>v.co), backgroundColor:"#90aed0", borderRadius:3, stack:"s" },
      ]},
      options:{
        responsive:true, maintainAspectRatio:false,
        plugins:{ legend:{display:false}, tooltip:{callbacks:{label:(ctx:any)=>`${ctx.dataset.label}: ${fmtTok(ctx.parsed.y)} tok`}}},
        scales:{
          x:{ticks:{font:{size:10},color:"#445566",autoSkip:false,maxRotation:30},grid:{display:false},stacked:true},
          y:{ticks:{font:{size:10},color:"#445566",callback:(v:any)=>v>=1000?`${(v/1000).toFixed(0)}k`:v},grid:{color:"#eef1f6"},stacked:true},
        },
      },
    });
    return () => { if (inst.current) inst.current.destroy(); };
  }, [entries]);
  if (!entries.length) return <div style={{fontSize:11,color:"#445566",fontStyle:"italic",padding:"10px 0"}}>No token usage recorded by job type yet.</div>;
  return <div style={{position:"relative",height:180}}><canvas ref={ref}/></div>;
}

// ─── AgenticUsageSection — added Entity column ────────────────────────────────
interface UsageAgg {
  totPr:number; totCo:number; totTok:number; totCost:number; ratio:number;
  byTypeTok:Record<string,{tok:number;pr:number;co:number;runs:number;succRuns:number}>;
  jobsWithUsage:any[];
}

export function AgenticUsageSection({ a, jobs, emap }: { a:UsageAgg; jobs:any[]; emap:EntityMap }) {
  const tableJobs = [...jobs].sort((a,b)=>parseServerTime(b.start_time).getTime()-parseServerTime(a.start_time).getTime());
  const scol = (s:string) => ({
    success:{ bg:"#f0fdf4",text:"#166534",border:"#86efac" },
    failed: { bg:"#fef2f2",text:"#991b1b",border:"#fca5a5" },
  }[(s||"").toLowerCase()] || { bg:"#eef1f6",text:"#445566",border:"#c8d4e0" });

  return (
    <>
      <div className="db-kpis" style={{marginBottom:14}}>
        {[
          { label:"Total tokens",      val:fmtTok(a.totTok),  sub:`${fmtTok(a.totPr)} prompt · ${fmtTok(a.totCo)} completion`, accent:"#1e3a6e" },
          { label:"Prompt tokens",     val:fmtTok(a.totPr),   sub:a.totTok>0?`${Math.round(a.totPr/a.totTok*100)}% of total`:"—", accent:"#6b21a8" },
          { label:"Completion tokens", val:fmtTok(a.totCo),   sub:a.totTok>0?`${Math.round(a.totCo/a.totTok*100)}% of total`:"—", accent:"#90aed0" },
          { label:"Estimated cost",    val:fmtUSD(a.totCost), sub:a.totCost===0?"Cost not tracked":"USD total", accent:a.totCost>0?"#166534":"#445566" },
        ].map((k,i)=>(
          <div className="db-kpi" key={i} style={{cursor:"default"}}>
            <div className="db-kpi-accent" style={{background:k.accent}}/>
            <div className="db-kpi-lbl">{k.label}</div>
            <div className="db-kpi-val" style={{color:k.accent}}>{k.val}</div>
            <div className="db-kpi-sub">{k.sub}</div>
          </div>
        ))}
      </div>

      {a.totTok === 0 ? (
        <div className="db-card" style={{marginBottom:12,padding:20,textAlign:"center"}}>
          <div style={{fontSize:13,color:"#445566",marginBottom:4}}>No LLM token usage recorded in this job list.</div>
          <div style={{fontSize:11,color:"#445566"}}>Token counts populate once agentic jobs complete successfully.</div>
        </div>
      ) : (
        <div className="db-row-h" style={{marginBottom:12}}>
          <div className="db-card">
            <div className="db-card-title">Tokens per job run <span>{a.jobsWithUsage.length} runs with LLM activity</span></div>
            <div style={{display:"flex",gap:12,marginBottom:8}}>
              {[{l:"Prompt",c:"#AFA9EC"},{l:"Completion",c:"#90aed0"}].map(i=>(
                <span key={i.l} style={{display:"flex",alignItems:"center",gap:4,fontSize:11,color:"#445566"}}>
                  <span style={{width:9,height:9,borderRadius:2,background:i.c,display:"inline-block"}}/>{i.l}
                </span>
              ))}
            </div>
            <TokenBarChart jobs={a.jobsWithUsage}/>
          </div>
          <div className="db-card">
            <div className="db-card-title">Prompt vs completion split</div>
            <div style={{marginBottom:14}}>
              <div style={{display:"flex",justifyContent:"space-between",fontSize:10,color:"#445566",marginBottom:4}}>
                <span>Token ratio</span>
                <span style={{fontFamily:"'IBM Plex Mono',monospace",fontWeight:700,color:"#1e3a6e"}}>{a.ratio}% / {100-a.ratio}%</span>
              </div>
              <div style={{height:10,borderRadius:99,overflow:"hidden",display:"flex",marginBottom:5}}>
                <div style={{width:`${a.ratio}%`,background:"#AFA9EC"}}/>
                <div style={{flex:1,background:"#90aed0"}}/>
              </div>
              <div style={{display:"flex",gap:14,fontSize:10,color:"#445566"}}>
                <span style={{display:"flex",alignItems:"center",gap:4}}>
                  <span style={{width:8,height:8,borderRadius:"50%",background:"#AFA9EC",display:"inline-block"}}/>Prompt {fmtTok(a.totPr)}
                </span>
                <span style={{display:"flex",alignItems:"center",gap:4}}>
                  <span style={{width:8,height:8,borderRadius:"50%",background:"#90aed0",display:"inline-block"}}/>Completion {fmtTok(a.totCo)}
                </span>
              </div>
            </div>
            <div style={{borderTop:`1px solid #c8d4e0`,paddingTop:12}}>
              <div className="db-card-title" style={{marginBottom:10}}>Tokens by job type</div>
              <TypeTokenChart byTypeTok={a.byTypeTok}/>
            </div>
          </div>
        </div>
      )}

      {/* ── Per-job table with Entity column ── */}
      <div className="db-card" style={{marginBottom:14}}>
        <div className="db-card-title">Per-job agentic usage <span>{tableJobs.length} total jobs</span></div>
        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
            <thead>
              <tr>
                {["Job type","Entity / System","Status","Prompt","Completion","Total","Cost","Duration"].map(h=>(
                  <th key={h} style={{padding:"6px 8px",background:"#eef1f6",borderBottom:"1px solid #c8d4e0",fontWeight:600,color:"#2c3e52",textAlign:"left",fontSize:11,whiteSpace:"nowrap"}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tableJobs.map((j,i)=>{
                const au  = j.agentic_usage || {};
                const tok = au.total_tokens || 0;
                const sc  = scol(j.status||"");
                const ei  = getEntityInfo(j, emap);
                const dur = j.start_time&&j.end_time
                  ? Math.round((parseServerTime(j.end_time).getTime()-parseServerTime(j.start_time).getTime())/1000) : null;
                const durStr = dur==null?"—":dur<60?`${dur}s`:`${Math.floor(dur/60)}m ${dur%60}s`;
                return (
                  <tr key={i}
                    onMouseEnter={e=>(e.currentTarget.style.background="#f5f7fa")}
                    onMouseLeave={e=>(e.currentTarget.style.background="transparent")}>
                    <td style={{padding:"6px 8px",borderBottom:"1px solid #eef1f6",fontSize:11,color:"#2c3e52",maxWidth:160,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}} title={j.job_type}>
                      {(j.job_type||"").replace(/DG_|DQ_/g,"").replace(/_/g," ").toLowerCase()}
                    </td>
                    {/* ── NEW: entity + system column ── */}
                    <td style={{padding:"6px 8px",borderBottom:"1px solid #eef1f6",maxWidth:160}}>
                      {ei ? (
                        <div>
                          <div style={{fontSize:11,color:T.teal,fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",maxWidth:150}} title={ei.label}>{ei.label}</div>
                          {ei.system && <div style={{fontSize:9.5,color:T.textMuted,fontFamily:"'IBM Plex Mono',monospace",marginTop:1}}>{ei.system}</div>}
                        </div>
                      ) : <span style={{fontSize:10,color:T.textMuted}}>—</span>}
                    </td>
                    <td style={{padding:"6px 8px",borderBottom:"1px solid #eef1f6"}}>
                      <span style={{display:"inline-flex",alignItems:"center",padding:"1px 7px",borderRadius:99,fontFamily:"'IBM Plex Mono',monospace",fontSize:9.5,fontWeight:500,border:"1px solid",background:sc.bg,color:sc.text,borderColor:sc.border}}>
                        {j.status||"—"}
                      </span>
                    </td>
                    <td style={{padding:"6px 8px",borderBottom:"1px solid #eef1f6",fontFamily:"'IBM Plex Mono',monospace",fontSize:11,color:tok>0?"#6b21a8":"#c8d4e0"}}>{fmtTok(au.prompt_tokens||0)}</td>
                    <td style={{padding:"6px 8px",borderBottom:"1px solid #eef1f6",fontFamily:"'IBM Plex Mono',monospace",fontSize:11,color:tok>0?"#90aed0":"#c8d4e0"}}>{fmtTok(au.completion_tokens||0)}</td>
                    <td style={{padding:"6px 8px",borderBottom:"1px solid #eef1f6",fontFamily:"'IBM Plex Mono',monospace",fontSize:11,fontWeight:tok>0?700:400,color:tok>0?"#1e3a6e":"#c8d4e0"}}>{fmtTok(tok)}</td>
                    <td style={{padding:"6px 8px",borderBottom:"1px solid #eef1f6",fontFamily:"'IBM Plex Mono',monospace",fontSize:11,color:"#445566"}}>{fmtUSD(au.cost_usd??null)}</td>
                    <td style={{padding:"6px 8px",borderBottom:"1px solid #eef1f6",fontFamily:"'IBM Plex Mono',monospace",fontSize:11,color:"#445566"}}>{durStr}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

// ─── KPIs (unchanged) ─────────────────────────────────────────────────────────
function KPIs({ a, nav }: { a:ReturnType<typeof useAgg>; nav:(o:any)=>void }) {
  const items = [
    { label:"Total jobs",   val:String(a.total),  sub:`${a.succ} succeeded · ${a.fail} failed`, hint:"View all jobs",    accent:T.brand, filter:{} },
    { label:"Success rate", val:a.total>0?`${Math.round(a.succ/a.total*100)}%`:"—", sub:`${a.succ} of ${a.total} jobs`, hint:"View succeeded", accent:T.green, filter:{statusFilter:"success"} },
    { label:"Failed jobs",  val:String(a.fail),   sub:a.fail>0?"Needs attention":"All clear",  hint:a.fail>0?"View failures":"", accent:a.fail>0?T.red:T.textMuted, filter:{statusFilter:"failed"} },
    { label:"DQ pass rate", val:a.dqHealth!=null?`${a.dqHealth}%`:"—",
      sub:a.totRules>0?`${a.totPass.toLocaleString()} / ${a.totRules.toLocaleString()} rules`:"No DQ rule runs",
      hint:a.dqJobs.length>0?"View DQ jobs":"",
      accent:a.dqHealth!=null&&a.dqHealth>=80?T.green:a.dqHealth!=null?T.amber:T.textMuted,
      filter:{jobTypeFilter:"DQ_RULE_EXECUTION"} },
  ];
  return (
    <div className="db-kpis">
      {items.map((k,i)=>(
        <div className="db-kpi" key={i} onClick={()=>nav(k.filter)}>
          <div className="db-kpi-accent" style={{background:k.accent}}/>
          <div className="db-kpi-lbl">{k.label}</div>
          <div className="db-kpi-val" style={{color:k.accent}}>{k.val}</div>
          <div className="db-kpi-sub">{k.sub}</div>
          {k.hint&&<div className="db-kpi-hint">{k.hint} →</div>}
        </div>
      ))}
    </div>
  );
}

function ByType({ byType, nav }: { byType:any; nav:(o:any)=>void }) {
  const entries = Object.entries(byType).sort((a:any,b:any)=>b[1].total-a[1].total) as [string,any][];
  const max = Math.max(...entries.map(e=>e[1].total),1);
  return (
    <div className="db-card">
      <div className="db-card-title">Jobs by type <span>{entries.length} types · click to filter</span></div>
      {entries.map(([t,v])=>(
        <div key={t} className="db-type-row" onClick={()=>nav({jobTypeFilter:t})}>
          <div className="db-hbar-label" title={t}>{t.replace(/DG_|DQ_/g,"").replace(/_/g," ").toLowerCase()}</div>
          <div className="db-hbar-track"><div className="db-hbar-fill" style={{width:`${Math.round(v.total/max*100)}%`,background:T.brand}}/></div>
          <div className="db-hbar-count">{v.total}</div>
          <div style={{display:"flex",gap:5,minWidth:44,flexShrink:0}}>
            {v.succ>0&&<span style={{fontSize:10,color:T.green}}>✓{v.succ}</span>}
            {v.fail>0&&<span style={{fontSize:10,color:T.red}}>✗{v.fail}</span>}
          </div>
        </div>
      ))}
      <div className="db-notice"><span style={{fontSize:13}}>↗</span> Click any type to filter the monitor view</div>
    </div>
  );
}

// ─── RecentJobs — entity chip added ──────────────────────────────────────────
function RecentJobs({ recent, navJob, emap }: { recent:any[]; navJob:(id:string)=>void; emap:EntityMap }) {
  return (
    <div className="db-card">
      <div className="db-card-title">Recent runs <span>last {recent.length}</span></div>
      {recent.map((j,i)=>{
        const c    = counts(j);
        const isAg = isAgentic(j);
        const ei   = getEntityInfo(j, emap);
        return (
          <div className="db-job-row" key={i} onClick={()=>navJob(j.job_id)} title="Open in monitor">
            <div className="db-job-dot" style={{background:sColor(j.status)}}/>
            <div style={{flex:1,minWidth:0}}>
              <div className="db-job-name" title={j.job_type}>{j.job_type||j.job_id}</div>
              {/* ── NEW: entity + system below job type ── */}
              {ei && (
                <div style={{marginTop:2,display:"flex",alignItems:"center",gap:4}}>
                  <span className="db-entity-chip" title={`${ei.system ? ei.system+" · " : ""}${ei.label}`}>
                    {ei.system ? `${ei.system} · ${ei.label}` : ei.label}
                  </span>
                </div>
              )}
            </div>
            <Pill s={j.status}/>
            {isAg&&<span className="db-pill" style={{background:T.purpleBg,color:T.purple,borderColor:T.purpleBorder,fontSize:9}}>agentic</span>}
            {c.total_rules>0&&<><span style={{fontSize:10,color:T.green}}>✓{c.pass_count}</span><span style={{fontSize:10,color:T.red}}>✗{c.fail_count}</span></>}
            <div className="db-job-meta">{fmtDur(j.start_time,j.end_time)}</div>
          </div>
        );
      })}
      <div className="db-notice"><span style={{fontSize:13}}>↗</span> Click any row to open job detail in monitor</div>
    </div>
  );
}

// ─── AgenticCard (unchanged logic, entity count hint added) ──────────────────
function AgenticCard({ a, nav }: { a:ReturnType<typeof useAgg>; nav:(o:any)=>void }) {
  const agSucc   = a.agJobs.filter(j=>j.status?.toLowerCase()==="success").length;
  const agFail   = a.agJobs.filter(j=>["failed","fail"].includes(j.status?.toLowerCase())).length;
  const chunkPct = a.totChunks>0?Math.round(a.succChunks/a.totChunks*100):null;
  const barC     = chunkPct!=null?(chunkPct===100?T.green:chunkPct>=70?T.amber:T.red):T.textMuted;
  return (
    <div className="db-card">
      <div className="db-card-title">Agentic jobs <Drill label="View all agentic" onClick={()=>nav({jobTypeFilter:"DG_BUSINESS_METADATA_ENRICH"})}/></div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:14}}>
        {[
          {v:a.agJobs.length,l:"agentic jobs",c:T.purple,filter:{}},
          {v:agSucc,l:"succeeded",c:T.green,filter:{statusFilter:"success"}},
          {v:agFail,l:"failed",c:agFail>0?T.red:T.textMuted,filter:{statusFilter:"failed"}},
          {v:a.running,l:"running",c:T.brand,filter:{statusFilter:"running"}},
        ].map((k,i)=>(
          <div key={i} style={{background:T.bg,borderRadius:7,padding:"8px 10px",cursor:"pointer",transition:"background .1s"}}
            onClick={()=>nav(k.filter)}
            onMouseEnter={e=>(e.currentTarget.style.background=T.brandLight)}
            onMouseLeave={e=>(e.currentTarget.style.background=T.bg)}>
            <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:20,fontWeight:700,color:k.c,lineHeight:1}}>{k.v}</div>
            <div style={{fontSize:10,color:T.textMuted,marginTop:2}}>{k.l}</div>
          </div>
        ))}
      </div>
      {chunkPct!=null?(
        <div>
          <div style={{display:"flex",justifyContent:"space-between",fontSize:10,color:T.textMuted,marginBottom:3}}>
            <span>LLM chunk success rate</span>
            <span style={{fontFamily:"'IBM Plex Mono',monospace",fontWeight:600,color:barC}}>{chunkPct}%</span>
          </div>
          <div style={{height:5,borderRadius:99,background:T.border,overflow:"hidden"}}>
            <div style={{height:"100%",width:`${chunkPct}%`,background:barC,borderRadius:99}}/>
          </div>
          <div style={{display:"flex",gap:12,marginTop:5,fontSize:10,color:T.textMuted}}>
            <span style={{color:T.green}}>✓ {a.succChunks} ok</span>
            {a.failChunks>0&&<span style={{color:T.red}}>✗ {a.failChunks} failed</span>}
            <span style={{marginLeft:"auto",color:T.textMuted}}>{a.totChunks} total</span>
          </div>
        </div>
      ):(
        <div style={{fontSize:11,color:T.textMuted,fontStyle:"italic",borderTop:`1px solid ${T.border}`,paddingTop:8}}>
          Chunk-level stats appear after opening individual job details in the monitor.
        </div>
      )}
    </div>
  );
}

// ─── DQCard — entity chip on ranked rows ─────────────────────────────────────
function DQCard({ a, nav, navJob, emap }: { a:ReturnType<typeof useAgg>; nav:(o:any)=>void; navJob:(id:string)=>void; emap:EntityMap }) {
  const h  = a.dqHealth;
  const hC = h==null?T.textMuted:h>=90?T.green:h>=70?T.amber:T.red;
  const C  = 2*Math.PI*28;
  const dash = h!=null?Math.round(h/100*C):0;
  return (
    <div className="db-card">
      <div className="db-card-title">DQ health <Drill label="View DQ jobs" onClick={()=>nav({jobTypeFilter:"DQ_RULE_EXECUTION"})}/></div>
      <div style={{display:"flex",alignItems:"center",gap:16,marginBottom:12}}>
        <svg width="72" height="72" viewBox="0 0 72 72" style={{flexShrink:0}}>
          <circle cx="36" cy="36" r="28" fill="none" stroke={T.border} strokeWidth="7"/>
          {h!=null&&<circle cx="36" cy="36" r="28" fill="none" stroke={hC} strokeWidth="7"
            strokeDasharray={`${dash} ${C}`} strokeLinecap="round" transform="rotate(-90 36 36)"/>}
          <text x="36" y="40" textAnchor="middle" fontFamily="'IBM Plex Mono',monospace" fontSize="13" fontWeight="700" fill={hC}>{h!=null?`${h}%`:"—"}</text>
        </svg>
        <div>
          <div style={{fontSize:11,color:T.textMuted,marginBottom:4}}>
            {a.totRules>0?`${a.dqJobs.length} DQ job run${a.dqJobs.length!==1?"s":""}`:
             "No DQ rule execution runs found yet"}
          </div>
          {a.totRules>0&&<>
            <div style={{fontSize:12,color:T.green}}>✓ {a.totPass.toLocaleString()} passed</div>
            <div style={{fontSize:12,color:T.red}}>✗ {a.totFail.toLocaleString()} failed</div>
            <div style={{fontSize:12,color:T.textMuted}}>{(a.totRules-a.totPass-a.totFail).toLocaleString()} other</div>
          </>}
        </div>
      </div>
      {a.dqRanked.length>0&&<>
        <div style={{fontSize:10,fontWeight:600,color:T.textMuted,textTransform:"uppercase",letterSpacing:".6px",marginBottom:8}}>DQ runs with rules</div>
        {a.dqRanked.map((j:any,i:number)=>{
          const bc = j._pct>=90?T.green:j._pct>=70?T.amber:T.red;
          const ei = getEntityInfo(j, emap);
          return (
            <div key={i} style={{marginBottom:10,cursor:"pointer"}} onClick={()=>navJob(j.job_id)}>
              <div style={{display:"flex",justifyContent:"space-between",fontSize:11,marginBottom:2}}>
                <div style={{minWidth:0,flex:1,marginRight:8}}>
                  <div style={{color:T.textLabel,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{j.job_type}</div>
                  {/* ── NEW: entity chip ── */}
                  {ei&&<span className="db-entity-chip" style={{marginTop:2,display:"inline-flex"}}>{ei.system?`${ei.system} · `:""}{ei.label}</span>}
                </div>
                <div style={{display:"flex",gap:8,alignItems:"center",flexShrink:0}}>
                  <span style={{fontSize:10,color:T.textMuted}}>{j._pass}/{j._total}</span>
                  <span style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:10,fontWeight:600,color:bc,minWidth:28,textAlign:"right"}}>{j._pct}%</span>
                </div>
              </div>
              <div style={{height:4,borderRadius:99,background:T.border,overflow:"hidden"}}>
                <div style={{height:"100%",width:`${j._pct}%`,background:bc,borderRadius:99}}/>
              </div>
            </div>
          );
        })}
      </>}
    </div>
  );
}

function StatusCard({ a, nav }: { a:ReturnType<typeof useAgg>; nav:(o:any)=>void }) {
  const items = [
    {label:"Succeeded",count:a.succ,   color:T.green, bg:T.greenBg,    status:"success"},
    {label:"Failed",   count:a.fail,   color:T.red,   bg:T.redBg,      status:"failed"},
    {label:"Running",  count:a.running,color:T.brand, bg:T.brandLight, status:"running"},
  ].filter(i=>i.count>0);
  return (
    <div className="db-card">
      <div className="db-card-title">Status breakdown</div>
      {items.map((it,i)=>(
        <div key={i} style={{display:"flex",alignItems:"center",gap:10,padding:"7px 5px",borderRadius:6,cursor:"pointer",marginBottom:2,transition:"background .12s"}}
          onClick={()=>nav({statusFilter:it.status})}
          onMouseEnter={e=>(e.currentTarget.style.background=T.brandLight)}
          onMouseLeave={e=>(e.currentTarget.style.background="transparent")}>
          <div style={{width:32,height:32,borderRadius:7,background:it.bg,display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'IBM Plex Mono',monospace",fontSize:15,fontWeight:700,color:it.color,flexShrink:0}}>{it.count}</div>
          <div style={{fontSize:12,color:T.textLabel,flex:1}}>{it.label}</div>
          <div style={{height:5,width:80,background:T.border,borderRadius:99,overflow:"hidden"}}>
            <div style={{height:"100%",width:`${Math.round(it.count/a.total*100)}%`,background:it.color,borderRadius:99}}/>
          </div>
          <div style={{fontFamily:"'IBM Plex Mono',monospace",fontSize:10,color:T.textMuted,width:28,textAlign:"right"}}>{Math.round(it.count/a.total*100)}%</div>
        </div>
      ))}
      <div className="db-notice"><span style={{fontSize:13}}>↗</span> Click status to filter the monitor view</div>
    </div>
  );
}

// ─── TimelineCard — entity in tooltip ────────────────────────────────────────
function TimelineCard({ timeline, navJob, emap }: { timeline:any[]; navJob:(id:string)=>void; emap:EntityMap }) {
  const durs = timeline.map(j => {
    if (!j.start_time||!j.end_time) return 0;
    return Math.round((parseServerTime(j.end_time).getTime()-parseServerTime(j.start_time).getTime())/1000);
  });
  const maxD = Math.max(...durs,1);
  const MIN=6, MAX=50;
  return (
    <div className="db-card">
      <div className="db-card-title">Job timeline <span>bar height = duration · click to open</span></div>
      <div style={{display:"flex",alignItems:"flex-end",gap:3,height:58,marginBottom:8}}>
        {timeline.map((j,i)=>{
          const h   = durs[i]>0?Math.round(MIN+(MAX-MIN)*(durs[i]/maxD)):MIN;
          const col = (j.status||"").toLowerCase()==="success"?T.brand:["failed","fail","error"].includes((j.status||"").toLowerCase())?T.red:T.amber;
          const time = j.start_time?parseServerTime(j.start_time).toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"}):"";
          const ei   = getEntityInfo(j, emap);
          // ── NEW: entity in tooltip ──
          const tip  = [j.job_type, ei?`${ei.system?ei.system+" · ":""}${ei.label}`:"", time, fmtDur(j.start_time,j.end_time), j.status].filter(Boolean).join("\n");
          return (
            <div key={j.job_id} title={tip}
              style={{flex:1,height:`${h}px`,background:col,borderRadius:"3px 3px 0 0",opacity:.8,cursor:"pointer",transition:"opacity .12s"}}
              onClick={()=>navJob(j.job_id)}
              onMouseEnter={e=>(e.currentTarget.style.opacity="1")}
              onMouseLeave={e=>(e.currentTarget.style.opacity=".8")}
            />
          );
        })}
      </div>
      <div style={{display:"flex",gap:14,fontSize:10,color:T.textMuted,flexWrap:"wrap"}}>
        {[{l:"Success",c:T.brand},{l:"Failed",c:T.red},{l:"Other",c:T.amber}].map(l=>(
          <span key={l.l} style={{display:"flex",alignItems:"center",gap:4}}>
            <span style={{width:8,height:8,borderRadius:2,background:l.c,display:"inline-block"}}/>{l.l}
          </span>
        ))}
      </div>
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export interface JobDashboardProps {
  jobs:                any[] | null;
  dmData?:             any[];           // ← NEW: entity list from parent
  onNavigateToMonitor: (opts: { statusFilter?:string; jobTypeFilter?:string; selectedJobId?:string }) => void;
  onRefresh?:          () => void;
}

export default function JobDashboard({ jobs, dmData, onNavigateToMonitor, onRefresh }: JobDashboardProps) {
  const a    = useAgg(jobs || []);
  const emap = useMemo(() => buildEntityMap(dmData), [dmData]);   // ← build once
  const nav  = useCallback((o:any)=>onNavigateToMonitor(o), [onNavigateToMonitor]);
  const navJob = useCallback((id:string)=>onNavigateToMonitor({selectedJobId:id}), [onNavigateToMonitor]);

  return (
    <div className="db">
      <style>{CSS}</style>
      <div className="db-topbar">
        <div>
          <div className="db-title">Pipeline dashboard</div>
          <div className="db-sub">
            All jobs · {a.total} total runs
            {a.running>0&&<span style={{color:T.brand,marginLeft:8}}>● {a.running} running</span>}
          </div>
          {onRefresh&&(
            <div><button className="db-drill" onClick={onRefresh} title="Refresh jobs" style={{marginLeft:8}}>↻ Refresh</button></div>
          )}
        </div>
      </div>

      {!jobs ? (
        <div className="db-empty"><div className="db-ring"/>Loading…</div>
      ) : !jobs.length ? (
        <div className="db-empty">No jobs found for this application.</div>
      ) : (
        <>
          <KPIs a={a} nav={nav}/>
          <div className="db-row2">
            <ByType byType={a.byType} nav={nav}/>
            <RecentJobs recent={a.recent} navJob={navJob} emap={emap}/>   {/* ← emap */}
          </div>
          <div className="db-row-h">
            <AgenticCard a={a} nav={nav}/>
            <DQCard a={a} nav={nav} navJob={navJob} emap={emap}/>         {/* ← emap */}
          </div>
          <div className="db-row-h">
            <TimelineCard timeline={a.timeline} navJob={navJob} emap={emap}/>  {/* ← emap */}
            <StatusCard a={a} nav={nav}/>
          </div>
          <div className="db-card" style={{marginBottom:14,padding:0}}>
            <div style={{padding:"13px 15px 0",borderBottom:`1px solid ${T.border}`}}>
              <div className="db-card-title" style={{paddingBottom:12}}>
                LLM usage — agentic jobs
                <span>{a.jobsWithUsage?.length??0} runs with activity</span>
              </div>
            </div>
            <div style={{padding:"14px 15px"}}>
              <AgenticUsageSection a={a} jobs={jobs||[]} emap={emap}/>   {/* ← emap */}
            </div>
          </div>
        </>
      )}
    </div>
  );
}